import { AxiosProgressEvent, CancelTokenSource } from 'axios';

import BaseAPI from 'utils/api/base';

import {
  ActivityListResponse as VatixActivityListResponse,
  MessageActivityResponse,
  ActivityResponse as VatixActivityResponse,
} from 'ui/types';

import { PaginationParams, APIRequest } from 'utils/api/types';

import { UserSearchResponse } from 'utils/api/modules/user/user.types';

import {
  TaskType,
  TaskListResponse,
  TaskListPaginatedResponse,
  CreateTaskData,
  CreateTaskFromTemplateData,
} from './tasks.types';

class TasksAPI extends BaseAPI {
  createTask = (data: TaskType): APIRequest<TaskType> => {
    const url = `/tasks/`;
    const method = 'POST';

    return this.request<TaskType>({
      url,
      method,
      data,
    });
  };

  createTaskFromTemplate = (taskId: string): APIRequest<CreateTaskFromTemplateData> => {
    const url = `/tasks/${taskId}/inspection/`;
    const method = 'POST';

    return this.request<CreateTaskFromTemplateData>({
      url,
      method,
    });
  };

  updateTask = (taskId: string, data: Partial<TaskListResponse>): APIRequest<TaskListResponse> => {
    const url = `/tasks/${taskId}/`;
    const method = 'PATCH';
    return this.request<TaskListResponse>({
      url,
      method,
      data,
    });
  };

  deleteTask = (taskId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/tasks/${taskId}/`;

    return this.request<void>({
      url,
      method,
    });
  };

  loadTaskDetails = (taskId: string): APIRequest<TaskListResponse> => {
    const url = `/tasks/${taskId}/`;
    const method = 'GET';

    return this.request<TaskListResponse>({
      url,
      method,
    });
  };

  loadActivitiesTask = (taskId: string, params: PaginationParams): APIRequest<VatixActivityListResponse> => {
    const method = 'GET';
    const url = `/tasks/${taskId}/activities/`;

    return this.request<VatixActivityListResponse>({
      url,
      method,
      params,
    });
  };

  addTaskMessage = (
    taskId: string,
    message: string,
    taggedUsers?: UserSearchResponse[]
  ): APIRequest<MessageActivityResponse> => {
    const method = 'POST';
    const url = `/tasks/${taskId}/comments/`;

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message, taggedUsers },
    });
  };

  uploadTaskFile = (
    taskId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<VatixActivityResponse> => {
    const method = 'POST';
    const url = `/tasks/${taskId}/activities/files/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<VatixActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
    });
  };

  removeTaskFile = (taskId: string, fileId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/tasks/${taskId}/activities/files/${fileId}/`;

    return this.request<void>({
      url,
      method,
    });
  };

  removeTaskMessage = (taskId: string, messageId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/tasks/${taskId}/comments/${messageId}/`;

    return this.request<void>({
      url,
      method,
    });
  };

  loadTasksAnswer = (inspection: string, question: string): APIRequest<TaskListPaginatedResponse> => {
    const method = 'GET';
    const url = `/tasks/`;

    return this.request<TaskListPaginatedResponse>({
      url,
      method,
      params: { inspection, question },
    });
  };

  createTaskForForm = (data: CreateTaskData): APIRequest<TaskListResponse> => {
    const url = '/tasks/';
    const method = 'POST';

    return this.request<TaskListResponse>({
      url,
      method,
      data,
    });
  };
}

export default TasksAPI;
